import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import {
  GoogleSheetsService,
  GoogleFile,
  GoogleSheet,
} from "../services/google-sheets.service";

// ---------- Constants ----------

export const COLUMN_POSITIONS = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

export const FIELD_LABELS: Record<string, string> = {
  orderNumber: "Número de pedido",
  total: "Total",
  subtotal: "Subtotal",
  shippingCost: "Costo de envío",
  currency: "Moneda",
  status: "Estado",
  paymentStatus: "Estado de pago",
  createdAt: "Fecha de creación",
  shopifyOrderId: "ID pedido Shopify",
  "customerData.email": "Email del cliente",
  "customerData.phone": "Teléfono del cliente",
  "customerData.first_name": "Nombre",
  "customerData.last_name": "Apellido",
  "customerData.address.address1": "Dirección",
  "customerData.address.city": "Ciudad",
  "customerData.address.province": "Departamento / Estado",
  "customerData.address.province_code": "Código departamento",
  "customerData.address.country_code": "País",
  lineItems: "Productos",
};

// ---------- Types ----------

type ExportMode = "ALL" | "ORDERS_ONLY" | "LEADS_ONLY";

interface GoogleSheetsState {
  isConnected: boolean;
  isEnabled: boolean;
  spreadsheetId: string;
  spreadsheetName: string;
  sheetId: string;
  sheetName: string;
  exportMode: ExportMode;
  columnMapping: Record<string, string>;
  includeHeaders: boolean;

  availableFiles: GoogleFile[];
  availableSheets: GoogleSheet[];
  availableFields: string[];

  isLoading: boolean;
  isSaving: boolean;
  isLoadingFiles: boolean;
  isLoadingSheets: boolean;
  isDisconnecting: boolean;
  error: string | null;
  initialSnapshot: string | null;
}

interface GoogleSheetsActions {
  initialize: () => Promise<void>;
  connectGoogle: () => Promise<void>;
  disconnect: () => Promise<void>;
  refreshFiles: () => Promise<void>;
  refreshSheets: () => Promise<void>;
  selectSpreadsheet: (id: string, name: string) => Promise<void>;
  selectSheet: (id: string, name: string) => void;
  setExportMode: (mode: ExportMode) => void;
  setEnabled: (enabled: boolean) => void;
  setColumnField: (column: string, field: string) => void;
  setIncludeHeaders: (value: boolean) => void;
  save: () => Promise<void>;
  discard: () => void;
}

type GoogleSheetsStore = GoogleSheetsState & GoogleSheetsActions;

// ---------- Snapshot ----------

function getSnapshot(state: GoogleSheetsState): string {
  return JSON.stringify({
    isEnabled: state.isEnabled,
    spreadsheetId: state.spreadsheetId,
    spreadsheetName: state.spreadsheetName,
    sheetId: state.sheetId,
    sheetName: state.sheetName,
    exportMode: state.exportMode,
    columnMapping: state.columnMapping,
    includeHeaders: state.includeHeaders,
  });
}

export const selectIsDirty = (state: GoogleSheetsStore): boolean => {
  if (!state.initialSnapshot) return false;
  return getSnapshot(state) !== state.initialSnapshot;
};

// ---------- Initial state ----------

const INITIAL_STATE: GoogleSheetsState = {
  isConnected: false,
  isEnabled: false,
  spreadsheetId: "",
  spreadsheetName: "",
  sheetId: "",
  sheetName: "",
  exportMode: "ALL",
  columnMapping: {},
  includeHeaders: true,

  availableFiles: [],
  availableSheets: [],
  availableFields: [],

  isLoading: true,
  isSaving: false,
  isLoadingFiles: false,
  isLoadingSheets: false,
  isDisconnecting: false,
  error: null,
  initialSnapshot: null,
};

// ---------- Store ----------

export const useGoogleSheetsStore = create<GoogleSheetsStore>()(
  devtools(
    immer((set, get) => ({
      ...INITIAL_STATE,

      initialize: async () => {
        set((s) => {
          s.isLoading = true;
          s.error = null;
        });
        try {
          const [config, fields] = await Promise.all([
            GoogleSheetsService.getConfig(),
            GoogleSheetsService.getAvailableFields(),
          ]);

          set((s) => {
            s.availableFields = fields;
            s.isLoading = false;

            if (config) {
              s.isConnected = config.isConnected;
              s.isEnabled = config.isEnabled;
              s.spreadsheetId = config.spreadsheetId ?? "";
              s.spreadsheetName = config.spreadsheetName ?? "";
              s.sheetId = config.sheetId ?? "";
              s.sheetName = config.sheetName ?? "";
              s.exportMode = config.exportMode;
              s.columnMapping = config.columnMapping ?? {};
              s.includeHeaders = config.includeHeaders ?? true;
            }

            s.initialSnapshot = getSnapshot(s);
          });

          // Load files and sheets in background if connected
          const { isConnected, spreadsheetId } = get();
          if (isConnected) {
            const store = get();
            void store.refreshFiles();
            if (spreadsheetId) {
              void store.refreshSheets();
            }
          }
        } catch (err) {
          set((s) => {
            s.error =
              err instanceof Error
                ? err.message
                : "Error al cargar la configuración";
            s.isLoading = false;
          });
        }
      },

      connectGoogle: async () => {
        // Open popup synchronously in the click-handler tick to avoid browser popup-blocker,
        // then navigate it to the real auth URL once the fetch resolves.
        const popup = window.open(
          "about:blank",
          "_blank",
          "width=600,height=700"
        );
        try {
          const url = await GoogleSheetsService.getAuthUrl();
          if (popup) {
            popup.location.href = url;
            const handler = (event: MessageEvent) => {
              if (event.origin !== window.location.origin) return;
              if (
                (event.data as { type?: string })?.type ===
                "GOOGLE_OAUTH_SUCCESS"
              ) {
                cleanup();
                void get().initialize();
              }
            };
            // Poll every 500 ms to detect manual close and remove the orphaned listener.
            const poll = setInterval(() => {
              if (popup.closed) cleanup();
            }, 500);
            const cleanup = () => {
              clearInterval(poll);
              window.removeEventListener("message", handler);
            };
            window.addEventListener("message", handler);
          } else {
            window.location.href = url;
          }
        } catch {
          popup?.close();
        }
      },

      disconnect: async () => {
        set((s) => {
          s.isDisconnecting = true;
        });
        try {
          await GoogleSheetsService.disconnect();
          set((s) => {
            s.isConnected = false;
            s.isEnabled = false;
            s.availableFiles = [];
            s.availableSheets = [];
            s.initialSnapshot = getSnapshot(s);
            s.isDisconnecting = false;
          });
        } catch (err) {
          set((s) => {
            s.isDisconnecting = false;
            s.error =
              err instanceof Error ? err.message : "Error al desconectar";
          });
          throw err;
        }
      },

      refreshFiles: async () => {
        set((s) => {
          s.isLoadingFiles = true;
        });
        try {
          const files = await GoogleSheetsService.listFiles();
          set((s) => {
            s.availableFiles = files;
            s.isLoadingFiles = false;
          });
        } catch {
          set((s) => {
            s.isLoadingFiles = false;
          });
        }
      },

      refreshSheets: async () => {
        const { spreadsheetId } = get();
        if (!spreadsheetId) return;

        set((s) => {
          s.isLoadingSheets = true;
        });
        try {
          const sheets = await GoogleSheetsService.listSheets(spreadsheetId);
          set((s) => {
            s.availableSheets = sheets;
            s.isLoadingSheets = false;
          });
        } catch {
          set((s) => {
            s.isLoadingSheets = false;
          });
        }
      },

      selectSpreadsheet: async (id, name) => {
        set((s) => {
          s.spreadsheetId = id;
          s.spreadsheetName = name;
          s.sheetId = "";
          s.sheetName = "";
          s.availableSheets = [];
        });
        if (id) {
          await get().refreshSheets();
        }
      },

      selectSheet: (id, name) => {
        set((s) => {
          s.sheetId = id;
          s.sheetName = name;
        });
      },

      setExportMode: (mode) => {
        set((s) => {
          s.exportMode = mode;
        });
      },

      setEnabled: (enabled) => {
        set((s) => {
          s.isEnabled = enabled;
        });
      },

      setColumnField: (column, field) => {
        set((s) => {
          if (field === "") {
            delete s.columnMapping[column];
          } else {
            s.columnMapping[column] = field;
          }
        });
      },

      setIncludeHeaders: (value) => {
        set((s) => {
          s.includeHeaders = value;
        });
      },

      save: async () => {
        const s = get();
        set((st) => {
          st.isSaving = true;
        });
        try {
          // Only include columns with a field assigned
          const cleanMapping = Object.fromEntries(
            Object.entries(s.columnMapping).filter(([, v]) => v !== "")
          );

          await GoogleSheetsService.saveConfig({
            spreadsheet_id: s.spreadsheetId,
            spreadsheet_name: s.spreadsheetName,
            sheet_id: s.sheetId,
            sheet_name: s.sheetName,
            export_mode: s.exportMode,
            column_mapping: cleanMapping,
            include_headers: s.includeHeaders,
            is_enabled: s.isEnabled,
          });

          set((st) => {
            st.initialSnapshot = getSnapshot(st);
            st.isSaving = false;
          });
        } catch (err) {
          set((st) => {
            st.isSaving = false;
          });
          throw err;
        }
      },

      discard: () => {
        set((s) => {
          if (s.initialSnapshot) {
            const initial = JSON.parse(
              s.initialSnapshot
            ) as Partial<GoogleSheetsState>;
            Object.assign(s, initial);
          }
        });
      },
    })),
    { name: "GoogleSheetsStore" }
  )
);
